﻿import React from 'react'
import { Button, Heading, Text, LoadingSpinner, Input, Tooltip } from '../../components/ui'
import { SortableTableHeader } from '../../components/ui/SortableTableHeader'
import { supabase } from '../../lib/supabase'
import { admDespesasService } from '../../services/admDespesas'
import { hasAdmNotaDownloadKey, runAdmStorageSignedDownload } from '../../utils/admDespesasStorageDownload'
import { matchesPaymentDatePeriod } from '../../utils/admPaymentDateFilter'
import { PaymentDatePeriodFilterFields } from '../../components/adm/PaymentDatePeriodFilterFields'
import { printService } from '../../services/printService'
import { logCreate, logDelete, logUpdate } from '../../services/userLogs'
import type { AdmContract, AdmContractService } from '../../types/admDespesas'
import type { AdmCategoria, AdmCentroCusto } from '../../types/admCadastros'
import type { PrintConfig, PrintColumn, PrintRow } from '../../types/print'
import { ArrowDownTrayIcon, DocumentIcon, PencilIcon, TrashIcon, PlusIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import AdmDespesasContractEditModal, {
  contractEditPatchFromModal,
  contractEditStateFromRecord,
  type ContractEditModalState,
} from '../../components/adm/AdmDespesasContractEditModal'

const PAGE_SIZE = 20

/** Rótulo unificado do filtro por coluna file_name (mesmo padrão em Despesas). */
const FILTRO_NOME_ARQUIVO = 'Nome do arquivo'
const FILTRO_NOME_ARQUIVO_TITLE = 'Filtra pelo texto da coluna file_name no cabeçalho.'

/** Pasta no bucket ADM para o documento do contrato (mesma que NF-e: `notas/`; RLS + createSignedUrl). */
const ADM_CONTRACTS_STORAGE_FOLDER = 'notas'

function fileNameQueryMatches(fileNameColumn: string | null | undefined, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return (fileNameColumn ?? '').toLowerCase().includes(q)
}

type PrintScope = 'page' | 'filtered'

function summarizePrintFilters(entries: Array<[string, string]>): Array<{ label: string; value: string }> {
  return entries
    .map(([label, value]) => [label, value.trim()] as const)
    .filter(([, value]) => value.length > 0)
    .map(([label, value]) => ({ label, value }))
}

function formatCurrency(value: number | null): string {
  if (value == null) return '—'
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('pt-BR')
}

function formatDocument(value: string | null): string {
  if (!value) return '—'
  const digits = value.replace(/\D/g, '')
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  }
  return value
}

const InfoTip: React.FC<{ content: React.ReactNode }> = ({ content }) => (
  <Tooltip content={content} position="bottom" constrainWidth className="inline-flex shrink-0 align-middle">
    <span className="inline-flex rounded-full p-0.5 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200">
      <InformationCircleIcon className="h-4 w-4" />
    </span>
  </Tooltip>
)

export type AdmDespesasContractsTabProps = {
  contracts: AdmContract[]
  allServices: AdmContractService[]
  categorias: AdmCategoria[]
  centrosCusto: AdmCentroCusto[]
  categoryMap: Map<string, string>
  costCenterMap: Map<string, string>
  canEdit: boolean
  canDelete: boolean
  userId: string | undefined
  onRefresh: () => void
  pendingDetailDocumentId?: string | null
  onPendingDetailHandled?: () => void
  onError: (message: string | null) => void
  getErrorMessage: (error: unknown) => string
}

type ContractsSubTab = 'contracts' | 'services'

type ServiceEditModalState = {
  id: string
  contractId: string
  contractLabel: string
  description: string
  serviceCode: string
  quantity: string
  unitValue: string
  totalValue: string
  categoryId: string
  costCenterId: string
}

const AdmDespesasContractsTab: React.FC<AdmDespesasContractsTabProps> = ({
  contracts,
  allServices,
  categorias,
  centrosCusto,
  categoryMap,
  costCenterMap,
  canEdit,
  canDelete,
  userId,
  onRefresh,
  pendingDetailDocumentId = null,
  onPendingDetailHandled,
  onError,
  getErrorMessage,
}) => {
  const [subTab, setSubTab] = React.useState<ContractsSubTab>('contracts')
  const [contractNumberFilter, setContractNumberFilter] = React.useState('')
  const [counterpartyFilter, setCounterpartyFilter] = React.useState('')
  const [dateFrom, setDateFrom] = React.useState('')
  const [dateTo, setDateTo] = React.useState('')
  const [paymentDateFrom, setPaymentDateFrom] = React.useState('')
  const [paymentDateTo, setPaymentDateTo] = React.useState('')
  const [contractsFileNameFilter, setContractsFileNameFilter] = React.useState('')
  const [servicesSearch, setServicesSearch] = React.useState('')
  const [servicesCategory, setServicesCategory] = React.useState('')
  const [servicesCostCenter, setServicesCostCenter] = React.useState('')

  const [contractsPage, setContractsPage] = React.useState(1)
  const [servicesPage, setServicesPage] = React.useState(1)
  const [contractsSort, setContractsSort] = React.useState('start_date')
  const [contractsSortOrder, setContractsSortOrder] = React.useState<'asc' | 'desc'>('desc')
  const [servicesSort, setServicesSort] = React.useState('description')
  const [servicesSortOrder, setServicesSortOrder] = React.useState<'asc' | 'desc'>('asc')

  const contractMap = React.useMemo(() => new Map(contracts.map((c) => [c.id, c])), [contracts])

  const toggleContractsSort = (key: string) => {
    if (contractsSort === key) {
      setContractsSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setContractsSort(key)
      setContractsSortOrder('asc')
    }
    setContractsPage(1)
  }

  const toggleServicesSort = (key: string) => {
    if (servicesSort === key) {
      setServicesSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setServicesSort(key)
      setServicesSortOrder('asc')
    }
    setServicesPage(1)
  }

  const filteredContracts = React.useMemo(() => {
    const num = contractNumberFilter.trim().toLowerCase()
    const cp = counterpartyFilter.trim().toLowerCase()
    const q = servicesSearch.trim().toLowerCase()
    const svcLevel = !!(q || servicesCategory || servicesCostCenter)
    return contracts.filter((c) => {
      if (num && !(c.contract_number ?? '').toLowerCase().includes(num)) return false
      if (cp && !(c.counterparty_name ?? '').toLowerCase().includes(cp) && !(c.counterparty_document ?? '').toLowerCase().includes(cp)) return false
      if (dateFrom && (c.start_date ?? '') < dateFrom) return false
      if (dateTo && (c.end_date ?? c.start_date ?? '') > dateTo) return false
      if (!fileNameQueryMatches(c.file_name, contractsFileNameFilter)) return false
      if (!matchesPaymentDatePeriod(c.payment_date, paymentDateFrom, paymentDateTo)) return false
      if (svcLevel) {
        const svcs = allServices.filter((s) => s.contract_id === c.id)
        if (svcs.length === 0) return false
        const any = svcs.some((s) => {
          if (q && !(s.description ?? '').toLowerCase().includes(q)) return false
          if (servicesCategory && s.category_id !== servicesCategory) return false
          if (servicesCostCenter && s.cost_center_id !== servicesCostCenter) return false
          return true
        })
        if (!any) return false
      }
      return true
    })
  }, [
    contracts,
    contractNumberFilter,
    counterpartyFilter,
    dateFrom,
    dateTo,
    paymentDateFrom,
    paymentDateTo,
    contractsFileNameFilter,
    allServices,
    servicesSearch,
    servicesCategory,
    servicesCostCenter,
  ])

  const runContractSignedDownloadCore = React.useCallback(
    async (
      fileNameStorage: string | null | undefined,
      fileName: string | null | undefined
    ): Promise<{ ok: true } | { ok: false; message: string }> =>
      runAdmStorageSignedDownload(supabase, {
        fileNameStorage,
        fileName,
        storageFolderPrefix: ADM_CONTRACTS_STORAGE_FOLDER,
        signedUrlTtlSec: 600,
      }),
    []
  )

  const openContractSignedDownload = React.useCallback(
    async (fileNameStorage: string | null | undefined, fileName: string | null | undefined) => {
      if (!hasAdmNotaDownloadKey(fileNameStorage, fileName)) return
      setContractDocDownloadBusy(true)
      onError(null)
      try {
        const r = await runContractSignedDownloadCore(fileNameStorage, fileName)
        if (!r.ok) onError(r.message)
      } finally {
        setContractDocDownloadBusy(false)
      }
    },
    [runContractSignedDownloadCore, onError]
  )

  const sortedContracts = React.useMemo(() => {
    return [...filteredContracts].sort((a, b) => {
      let av: string | number = ''
      let bv: string | number = ''
      if (contractsSort === 'contract_number') {
        av = (a.contract_number ?? '').toLowerCase()
        bv = (b.contract_number ?? '').toLowerCase()
      } else if (contractsSort === 'counterparty_name') {
        av = (a.counterparty_name ?? '').toLowerCase()
        bv = (b.counterparty_name ?? '').toLowerCase()
      } else if (contractsSort === 'start_date') {
        av = a.start_date ?? ''
        bv = b.start_date ?? ''
      } else if (contractsSort === 'end_date') {
        av = a.end_date ?? ''
        bv = b.end_date ?? ''
      } else if (contractsSort === 'total_value') {
        av = a.total_value ?? 0
        bv = b.total_value ?? 0
      }
      if (av < bv) return contractsSortOrder === 'asc' ? -1 : 1
      if (av > bv) return contractsSortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredContracts, contractsSort, contractsSortOrder])

  const contractsPagesCount = Math.max(1, Math.ceil(sortedContracts.length / PAGE_SIZE))
  const contractsStart = (contractsPage - 1) * PAGE_SIZE
  const paginatedContracts = sortedContracts.slice(contractsStart, contractsStart + PAGE_SIZE)

  const filteredServices = React.useMemo(() => {
    const q = servicesSearch.trim().toLowerCase()
    return allServices.filter((s) => {
      const header = contractMap.get(s.contract_id)
      if (!header) return false
      if (q && !(s.description ?? '').toLowerCase().includes(q)) return false
      if (servicesCategory && s.category_id !== servicesCategory) return false
      if (servicesCostCenter && s.cost_center_id !== servicesCostCenter) return false
      if (contractNumberFilter && !(header.contract_number ?? '').toLowerCase().includes(contractNumberFilter.trim().toLowerCase())) return false
      const cp = counterpartyFilter.trim().toLowerCase()
      if (cp && !(header.counterparty_name ?? '').toLowerCase().includes(cp) && !(header.counterparty_document ?? '').toLowerCase().includes(cp))
        return false
      if (dateFrom && (header.start_date ?? '') < dateFrom) return false
      if (dateTo && (header.end_date ?? header.start_date ?? '') > dateTo) return false
      if (!fileNameQueryMatches(header.file_name, contractsFileNameFilter)) return false
      if (!matchesPaymentDatePeriod(header.payment_date, paymentDateFrom, paymentDateTo)) return false
      return true
    })
  }, [
    allServices,
    contractMap,
    servicesSearch,
    servicesCategory,
    servicesCostCenter,
    contractNumberFilter,
    counterpartyFilter,
    dateFrom,
    dateTo,
    paymentDateFrom,
    paymentDateTo,
    contractsFileNameFilter,
  ])

  const sortedServices = React.useMemo(() => {
    return [...filteredServices].sort((a, b) => {
      const ha = contractMap.get(a.contract_id)
      const hb = contractMap.get(b.contract_id)
      let av: string | number = ''
      let bv: string | number = ''
      if (servicesSort === 'counterparty_name') {
        av = (ha?.counterparty_name ?? '').toLowerCase()
        bv = (hb?.counterparty_name ?? '').toLowerCase()
      } else if (servicesSort === 'description') {
        av = (a.description ?? '').toLowerCase()
        bv = (b.description ?? '').toLowerCase()
      } else if (servicesSort === 'quantity') {
        av = a.quantity ?? 0
        bv = b.quantity ?? 0
      } else if (servicesSort === 'unit_value') {
        av = a.unit_value ?? 0
        bv = b.unit_value ?? 0
      } else if (servicesSort === 'total_value') {
        av = a.total_value ?? 0
        bv = b.total_value ?? 0
      }
      if (av < bv) return servicesSortOrder === 'asc' ? -1 : 1
      if (av > bv) return servicesSortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredServices, contractMap, servicesSort, servicesSortOrder])

  const servicesPagesCount = Math.max(1, Math.ceil(sortedServices.length / PAGE_SIZE))
  const servicesStart = (servicesPage - 1) * PAGE_SIZE
  const paginatedServices = sortedServices.slice(servicesStart, servicesStart + PAGE_SIZE)

  const servicesCountByContract = React.useMemo(() => {
    const m = new Map<string, number>()
    for (const s of allServices) {
      m.set(s.contract_id, (m.get(s.contract_id) ?? 0) + 1)
    }
    return m
  }, [allServices])

  const contractsNotesTotal = React.useMemo(
    () => filteredContracts.reduce((s, c) => s + (c.total_value ?? 0), 0),
    [filteredContracts],
  )
  const servicesTotalValue = React.useMemo(
    () => filteredServices.reduce((s, i) => s + (i.total_value ?? 0), 0),
    [filteredServices],
  )

  const [detail, setDetail] = React.useState<{ header: AdmContract; services: AdmContractService[] } | null>(null)
  const [loadingDetail, setLoadingDetail] = React.useState(false)
  const [contractEditModal, setContractEditModal] = React.useState<ContractEditModalState | null>(null)
  const [submittingContractEdit, setSubmittingContractEdit] = React.useState(false)
  const [contractDocDownloadBusy, setContractDocDownloadBusy] = React.useState(false)

  const [serviceEditModal, setServiceEditModal] = React.useState<ServiceEditModalState | null>(null)
  const [submittingServiceEdit, setSubmittingServiceEdit] = React.useState(false)

  const [deleteContractModal, setDeleteContractModal] = React.useState<{ id: string; label: string } | null>(null)
  const [deleteContractReason, setDeleteContractReason] = React.useState('')
  const [submittingContractDelete, setSubmittingContractDelete] = React.useState(false)

  const [savingServiceId, setSavingServiceId] = React.useState<string | null>(null)
  const [printDialogOpen, setPrintDialogOpen] = React.useState(false)

  const openDetail = async (c: AdmContract) => {
    setLoadingDetail(true)
    onError(null)
    try {
      const services = await admDespesasService.listContractServices(c.id)
      setDetail({ header: c, services })
    } catch (e) {
      onError(getErrorMessage(e))
    } finally {
      setLoadingDetail(false)
    }
  }

  const openContractEdit = (c: AdmContract) => {
    setContractEditModal(contractEditStateFromRecord(c))
  }

  React.useEffect(() => {
    if (!pendingDetailDocumentId) return
    const record = contracts.find((c) => c.id === pendingDetailDocumentId)
    if (record) void openDetail(record)
    onPendingDetailHandled?.()
  }, [pendingDetailDocumentId, contracts, onPendingDetailHandled])

  const handleSaveContractEdit = async () => {
    if (!contractEditModal) return
    setSubmittingContractEdit(true)
    onError(null)
    try {
      const patch = contractEditPatchFromModal(contractEditModal)
      const prev = contracts.find((x) => x.id === contractEditModal.id)
      await admDespesasService.updateContract(contractEditModal.id, patch)
      await logUpdate(
        'other',
        contractEditModal.id,
        `Atualizou contrato ${patch.contract_number ?? contractEditModal.id}`,
        {
          contract_number: prev?.contract_number ?? null,
          counterparty_name: prev?.counterparty_name ?? null,
          counterparty_document: prev?.counterparty_document ?? null,
          start_date: prev?.start_date ?? null,
          end_date: prev?.end_date ?? null,
          total_value: prev?.total_value ?? null,
          notes: prev?.notes ?? null,
          objective: prev?.objective ?? null,
          signature_date: prev?.signature_date ?? null,
          payment_notes: prev?.payment_notes ?? null,
          payment_date: prev?.payment_date ?? null,
          manual_notes: prev?.manual_notes ?? null,
          contractor_name: prev?.contractor_name ?? null,
          contractor_document: prev?.contractor_document ?? null,
          import_method: prev?.import_method ?? null,
          file_name: prev?.file_name ?? null,
          file_name_storage: prev?.file_name_storage ?? null,
          payload_json: prev?.payload_json ?? null,
        },
        patch,
        { additional_info: 'Módulo ADM • Despesas (contrato)' },
      )
      setDetail((d) =>
        d && d.header.id === contractEditModal.id
          ? {
              ...d,
              header: {
                ...d.header,
                ...patch,
                contract_number: patch.contract_number ?? null,
                counterparty_name: patch.counterparty_name ?? null,
                counterparty_document: patch.counterparty_document ?? null,
                start_date: patch.start_date ?? null,
                end_date: patch.end_date ?? null,
                total_value: patch.total_value ?? null,
                notes: patch.notes ?? null,
                objective: patch.objective ?? null,
                signature_date: patch.signature_date ?? null,
                payment_notes: patch.payment_notes ?? null,
                payment_date: patch.payment_date ?? null,
                manual_notes: patch.manual_notes ?? null,
                contractor_name: patch.contractor_name ?? null,
                contractor_document: patch.contractor_document ?? null,
                import_method: patch.import_method ?? null,
              },
            }
          : d,
      )
      setContractEditModal(null)
      onRefresh()
    } catch (e) {
      onError(getErrorMessage(e))
    } finally {
      setSubmittingContractEdit(false)
    }
  }

  const confirmDeleteContract = async () => {
    if (!deleteContractModal || !userId) return
    const reason = deleteContractReason.trim()
    if (!reason) return
    setSubmittingContractDelete(true)
    onError(null)
    try {
      await admDespesasService.softDeleteContract(deleteContractModal.id, reason, userId)
      await logDelete('other', deleteContractModal.id, `Excluiu contrato: ${deleteContractModal.label}`, { additional_info: `Motivo: ${reason}` })
      setDeleteContractModal(null)
      setDeleteContractReason('')
      setDetail((prev) => (prev?.header.id === deleteContractModal.id ? null : prev))
      onRefresh()
    } catch (e) {
      onError(getErrorMessage(e))
    } finally {
      setSubmittingContractDelete(false)
    }
  }

  const handleAddService = async () => {
    if (!detail || !userId) return
    onError(null)
    try {
      const row = await admDespesasService.insertContractService({
        contract_id: detail.header.id,
        description: 'Novo serviço',
      })
      setDetail((prev) => (prev ? { ...prev, services: [...prev.services, row] } : prev))
      onRefresh()
      await logCreate(
        'other',
        row.id,
        `Adicionou linha de serviço ao contrato ${detail.header.contract_number ?? detail.header.id}`,
        {
          additional_info: `Módulo ADM • Despesas (contrato)`,
          new_values: {
            contract_id: detail.header.id,
            description: row.description ?? 'Novo serviço',
          },
        },
      )
    } catch (e) {
      onError(getErrorMessage(e))
    }
  }

  const openServiceEdit = (s: AdmContractService, contractLabel: string) => {
    setServiceEditModal({
      id: s.id,
      contractId: s.contract_id,
      contractLabel,
      description: s.description ?? '',
      serviceCode: s.service_code ?? '',
      quantity: s.quantity != null ? String(s.quantity) : '',
      unitValue: s.unit_value != null ? String(s.unit_value) : '',
      totalValue: s.total_value != null ? String(s.total_value) : '',
      categoryId: s.category_id ?? '',
      costCenterId: s.cost_center_id ?? '',
    })
  }

  const handleSaveServiceEdit = async () => {
    if (!serviceEditModal) return
    setSubmittingServiceEdit(true)
    onError(null)
    try {
      const categoryId = serviceEditModal.categoryId.trim() || null
      const costCenterId = serviceEditModal.costCenterId.trim() || null
      const patch = {
        description: serviceEditModal.description.trim() || null,
        service_code: serviceEditModal.serviceCode.trim() || null,
        quantity: serviceEditModal.quantity.trim() ? Number(serviceEditModal.quantity) : null,
        unit_value: serviceEditModal.unitValue.trim() ? Number(serviceEditModal.unitValue) : null,
        total_value: serviceEditModal.totalValue.trim() ? Number(serviceEditModal.totalValue) : null,
        category_id: categoryId,
        cost_center_id: costCenterId,
      }
      const prev = allServices.find((x) => x.id === serviceEditModal.id)
      await admDespesasService.updateContractService(serviceEditModal.id, patch)
      await logUpdate(
        'other',
        serviceEditModal.id,
        `Atualizou serviço contratado (${serviceEditModal.contractLabel})`,
        {
          description: prev?.description ?? null,
          service_code: prev?.service_code ?? null,
          quantity: prev?.quantity ?? null,
          unit_value: prev?.unit_value ?? null,
          total_value: prev?.total_value ?? null,
          category_id: prev?.category_id ?? null,
          cost_center_id: prev?.cost_center_id ?? null,
        },
        patch,
        { additional_info: `Contrato ID: ${serviceEditModal.contractId}` },
      )
      setServiceEditModal(null)
      setDetail((prev) =>
        prev
          ? {
              ...prev,
              services: prev.services.map((i) => (i.id === serviceEditModal.id ? { ...i, ...patch } : i)),
            }
          : prev,
      )
      onRefresh()
    } catch (e) {
      onError(getErrorMessage(e))
    } finally {
      setSubmittingServiceEdit(false)
    }
  }

  const handleSoftDeleteService = async (item: AdmContractService, contractLabel: string) => {
    if (!userId) return
    const confirmed = window.confirm(`Confirma a exclusão do serviço "${item.description ?? item.id}"?`)
    if (!confirmed) return
    setSavingServiceId(item.id)
    onError(null)
    try {
      await admDespesasService.softDeleteContractService(item.id, userId, null)
      setDetail((prev) => (prev ? { ...prev, services: prev.services.filter((i) => i.id !== item.id) } : prev))
      await logDelete('other', item.id, `Excluiu serviço contratado (${contractLabel})`, {
        additional_info: `Contrato ID: ${item.contract_id}`,
        old_values: { description: item.description ?? null },
      })
      onRefresh()
    } catch (e) {
      onError(getErrorMessage(e))
    } finally {
      setSavingServiceId(null)
    }
  }

  const subTabClass = (active: boolean) =>
    active
      ? 'border-primary-500 text-primary-600 dark:text-primary-400 whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm'
      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm'

  const renderPagination = (page: number, pages: number, total: number, setPage: (n: number) => void) => (
    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
      <Text size="sm" color="muted">
        {total === 0 ? '0 registros' : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} de ${total}`}
      </Text>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
          Anterior
        </Button>
        <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>
          Próxima
        </Button>
      </div>
    </div>
  )

  const renderTotalsSummary = (items: { label: string; value: number; kind?: 'quantity' }[]) => (
    <div className="flex flex-wrap gap-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg px-4 py-3 border border-gray-200 dark:border-gray-700">
      {items.map((it) => (
        <div key={it.label}>
          <Text size="xs" color="muted" className="uppercase tracking-wide">
            {it.label}
          </Text>
          <Text size="sm" className="font-semibold text-gray-900 dark:text-white">
            {it.kind === 'quantity' ? it.value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 6 }) : formatCurrency(it.value)}
          </Text>
        </div>
      ))}
    </div>
  )

  const buildPrintPayload = (scope: PrintScope): PrintConfig => {
    const generatedAt = new Date().toLocaleString('pt-BR')
    const scopeLabel = scope === 'page' ? 'Página atual' : 'Todos os filtrados'

    const filterEntries: Array<[string, string]> = [
      ['Nº contrato', contractNumberFilter],
      ['Contraparte', counterpartyFilter],
      [FILTRO_NOME_ARQUIVO, contractsFileNameFilter],
      ['Data início', dateFrom],
      ['Data fim', dateTo],
      ['Pagamento de', paymentDateFrom],
      ['Pagamento até', paymentDateTo],
      ['Categoria', servicesCategory ? (categoryMap.get(servicesCategory) ?? servicesCategory) : ''],
      ['Centro de Custo', servicesCostCenter ? (costCenterMap.get(servicesCostCenter) ?? servicesCostCenter) : ''],
    ]
    if (subTab === 'services') {
      filterEntries.push(['Descrição serviço', servicesSearch])
    }

    if (subTab === 'contracts') {
      const source = scope === 'page' ? paginatedContracts : sortedContracts
      const columns: PrintColumn[] = [
        { key: 'contract_number', label: 'Número' },
        { key: 'counterparty_name', label: 'Contraparte' },
        { key: 'signature_date', label: 'Assinatura' },
        { key: 'contractor_name', label: 'Contratante' },
        { key: 'import_method', label: 'Importação' },
        { key: 'start_date', label: 'Início' },
        { key: 'end_date', label: 'Fim' },
        { key: 'services_count', label: 'Nº serviços', align: 'right' },
        { key: 'total_value', label: 'Valor total', align: 'right' },
      ]
      const rows: PrintRow[] = source.map((c) => ({
        contract_number: c.contract_number || '—',
        counterparty_name: c.counterparty_name || '—',
        signature_date: formatDate(c.signature_date),
        contractor_name: c.contractor_name || '—',
        import_method: c.import_method || '—',
        start_date: formatDate(c.start_date),
        end_date: formatDate(c.end_date),
        services_count: servicesCountByContract.get(c.id) ?? 0,
        total_value: formatCurrency(c.total_value),
      }))
      return {
        reportTitle: 'ADM • Despesas • Contratos • Contratos',
        generatedAt,
        scopeLabel,
        totalRows: rows.length,
        filters: summarizePrintFilters(filterEntries),
        columns,
        rows,
      }
    }

    const source = scope === 'page' ? paginatedServices : sortedServices
    const columns: PrintColumn[] = [
      { key: 'counterparty_name', label: 'Contraparte' },
      { key: 'description', label: 'Descrição' },
      { key: 'quantity', label: 'Qtd', align: 'right' },
      { key: 'unit_value', label: 'Vl. unit.', align: 'right' },
      { key: 'total_value', label: 'Total', align: 'right' },
      { key: 'category', label: 'Categoria' },
      { key: 'cost_center', label: 'Centro de Custo' },
    ]
    const rows: PrintRow[] = source.map((item) => {
      const header = contractMap.get(item.contract_id)
      return {
        counterparty_name: header?.counterparty_name || '—',
        description: item.description || '—',
        quantity: item.quantity ?? '—',
        unit_value: formatCurrency(item.unit_value),
        total_value: formatCurrency(item.total_value),
        category: item.category_id ? (categoryMap.get(item.category_id) ?? item.category_id) : '—',
        cost_center: item.cost_center_id ? (costCenterMap.get(item.cost_center_id) ?? item.cost_center_id) : '—',
      }
    })
    return {
      reportTitle: 'ADM • Despesas • Contratos • Serviços',
      generatedAt,
      scopeLabel,
      totalRows: rows.length,
      filters: summarizePrintFilters(filterEntries),
      columns,
      rows,
    }
  }

  const handleContractPrint = (scope: PrintScope) => {
    printService.print(buildPrintPayload(scope))
    setPrintDialogOpen(false)
  }

  return (
    <>
      <Text size="sm" color="muted" className="mb-2 block">
        Contratos e serviços, com filtros e detalhamento.
      </Text>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="mb-3">
          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
            Filtros
          </Heading>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 [&>*]:min-w-0">
          <Input
            type="text"
            placeholder="Nº do contrato"
            className="min-w-0"
            value={contractNumberFilter}
            onChange={(e) => {
              setContractNumberFilter(e.target.value)
              setContractsPage(1)
              setServicesPage(1)
            }}
          />
          <Input
            type="text"
            placeholder="Contraparte"
            title="Filtrar por nome ou documento da contraparte"
            className="min-w-0"
            value={counterpartyFilter}
            onChange={(e) => {
              setCounterpartyFilter(e.target.value)
              setContractsPage(1)
              setServicesPage(1)
            }}
          />
          <Input
            type="text"
            placeholder={FILTRO_NOME_ARQUIVO}
            title={FILTRO_NOME_ARQUIVO_TITLE}
            className="min-w-0"
            value={contractsFileNameFilter}
            onChange={(e) => {
              setContractsFileNameFilter(e.target.value)
              setContractsPage(1)
              setServicesPage(1)
            }}
          />
          <Input
            type="text"
            placeholder="Descrição do serviço"
            title="Filtra pela descrição das linhas de serviço (ambas as subabas)"
            className="min-w-0"
            value={servicesSearch}
            onChange={(e) => {
              setServicesSearch(e.target.value)
              setContractsPage(1)
              setServicesPage(1)
            }}
          />
          <div
            className="flex min-w-0 items-center gap-2"
            title="Data inicial da vigência (inclusiva)"
          >
            <span className="shrink-0 text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">
              Início
            </span>
            <Input
              type="date"
              className="min-w-0 flex-1"
              aria-label="Data inicial da vigência"
              value={dateFrom}
              onChange={(e) => {
              setDateFrom(e.target.value)
              setContractsPage(1)
              setServicesPage(1)
            }}
            />
          </div>
          <div
            className="flex min-w-0 items-center gap-2"
            title="Data final da vigência (inclusiva)"
          >
            <span className="shrink-0 text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">
              Fim
            </span>
            <Input
              type="date"
              className="min-w-0 flex-1"
              aria-label="Data final da vigência"
              value={dateTo}
              onChange={(e) => {
              setDateTo(e.target.value)
              setContractsPage(1)
              setServicesPage(1)
            }}
            />
          </div>
          <PaymentDatePeriodFilterFields
            from={paymentDateFrom}
            to={paymentDateTo}
            onFromChange={setPaymentDateFrom}
            onToChange={setPaymentDateTo}
            onChange={() => {
              setContractsPage(1)
              setServicesPage(1)
            }}
          />
          <select
            value={servicesCategory}
            onChange={(e) => {
              setServicesCategory(e.target.value)
              setContractsPage(1)
              setServicesPage(1)
            }}
            className="min-w-0 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm"
          >
            <option value="">Categoria: Todas</option>
            {categorias.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            value={servicesCostCenter}
            onChange={(e) => {
              setServicesCostCenter(e.target.value)
              setContractsPage(1)
              setServicesPage(1)
            }}
            className="min-w-0 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm"
          >
            <option value="">Centro de custo: Todos</option>
            {centrosCusto.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <div className="col-span-full flex min-w-0 flex-wrap items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setContractNumberFilter('')
                setCounterpartyFilter('')
                setContractsFileNameFilter('')
                setDateFrom('')
                setDateTo('')
                setPaymentDateFrom('')
                setPaymentDateTo('')
                setServicesSearch('')
                setServicesCategory('')
                setServicesCostCenter('')
                setContractsPage(1)
                setServicesPage(1)
              }}
            >
              Limpar filtros
            </Button>
            <Button variant="outline" size="sm" type="button" onClick={() => setPrintDialogOpen(true)}>
              <ArrowDownTrayIcon className="h-4 w-4" />
              Imprimir
            </Button>
          </div>
        </div>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-6">
          <button type="button" onClick={() => setSubTab('contracts')} className={subTabClass(subTab === 'contracts')}>
            <span className="inline-flex items-center gap-1.5">
              Contratos
              <InfoTip content="Contratos com contraparte, vigência e valor total." />
            </span>
          </button>
          <button type="button" onClick={() => setSubTab('services')} className={subTabClass(subTab === 'services')}>
            <span className="inline-flex items-center gap-1.5">
              Serviços
              <InfoTip content="Linhas de serviço vinculadas a cada contrato." />
            </span>
            {filteredServices.length > 0 && (
              <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                {filteredServices.length}
              </span>
            )}
          </button>
        </nav>
      </div>

      {subTab === 'contracts' && (
        <>
          {renderTotalsSummary([{ label: 'Soma valor total', value: contractsNotesTotal }])}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
            {paginatedContracts.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <DocumentIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
                <Text size="sm" color="muted">
                  {contracts.length === 0 ? 'Nenhum contrato cadastrado.' : 'Nenhum contrato encontrado com os filtros aplicados.'}
                </Text>
              </div>
            ) : (
              <>
                <table className="w-full min-w-[800px] divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <SortableTableHeader sortKey="contract_number" currentSort={contractsSort} sortOrder={contractsSortOrder} onSort={toggleContractsSort} className="px-4 py-3">
                        Número
                      </SortableTableHeader>
                      <SortableTableHeader sortKey="counterparty_name" currentSort={contractsSort} sortOrder={contractsSortOrder} onSort={toggleContractsSort} className="px-4 py-3">
                        Contraparte
                      </SortableTableHeader>
                      <SortableTableHeader sortKey="start_date" currentSort={contractsSort} sortOrder={contractsSortOrder} onSort={toggleContractsSort} className="px-4 py-3">
                        Início
                      </SortableTableHeader>
                      <SortableTableHeader sortKey="end_date" currentSort={contractsSort} sortOrder={contractsSortOrder} onSort={toggleContractsSort} className="px-4 py-3">
                        Fim
                      </SortableTableHeader>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Nº serviços</th>
                      <SortableTableHeader sortKey="total_value" currentSort={contractsSort} sortOrder={contractsSortOrder} onSort={toggleContractsSort} className="px-4 py-3 text-right">
                        Valor total
                      </SortableTableHeader>
                      {(canDelete || canEdit) && <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ações</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {paginatedContracts.map((c) => (
                      <tr key={c.id} onClick={() => void openDetail(c)} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer">
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">{c.contract_number || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-[280px] truncate" title={c.counterparty_name ?? ''}>
                          {c.counterparty_name || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">{formatDate(c.start_date)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">{formatDate(c.end_date)}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{servicesCountByContract.get(c.id) ?? 0}</td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">{formatCurrency(c.total_value)}</td>
                        {(canDelete || canEdit) && (
                          <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-center gap-2">
                              {canEdit && (
                                <button
                                  type="button"
                                  title="Editar contrato"
                                  onClick={() => openContractEdit(c)}
                                  className="text-primary-500 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                              )}
                              {canDelete && (
                                <button
                                  type="button"
                                  title="Excluir"
                                  onClick={() => {
                                    setDeleteContractModal({ id: c.id, label: c.contract_number || c.id })
                                    setDeleteContractReason('')
                                  }}
                                  className="text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {renderPagination(contractsPage, contractsPagesCount, sortedContracts.length, setContractsPage)}
              </>
            )}
          </div>
        </>
      )}

      {subTab === 'services' && (
        <>
          {renderTotalsSummary([{ label: 'Soma total linhas', value: servicesTotalValue }])}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
            {paginatedServices.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <DocumentIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
                <Text size="sm" color="muted">
                  {allServices.length === 0 ? 'Nenhum serviço cadastrado.' : 'Nenhum serviço encontrado com os filtros aplicados.'}
                </Text>
              </div>
            ) : (
              <>
                <table className="w-full min-w-[900px] divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <SortableTableHeader sortKey="counterparty_name" currentSort={servicesSort} sortOrder={servicesSortOrder} onSort={toggleServicesSort} className="px-4 py-3">
                        Contraparte
                      </SortableTableHeader>
                      <SortableTableHeader sortKey="description" currentSort={servicesSort} sortOrder={servicesSortOrder} onSort={toggleServicesSort} className="px-4 py-3">
                        Descrição
                      </SortableTableHeader>
                      <SortableTableHeader sortKey="quantity" currentSort={servicesSort} sortOrder={servicesSortOrder} onSort={toggleServicesSort} className="px-4 py-3 text-right">
                        Qtd
                      </SortableTableHeader>
                      <SortableTableHeader sortKey="unit_value" currentSort={servicesSort} sortOrder={servicesSortOrder} onSort={toggleServicesSort} className="px-4 py-3 text-right">
                        Vl. unit.
                      </SortableTableHeader>
                      <SortableTableHeader sortKey="total_value" currentSort={servicesSort} sortOrder={servicesSortOrder} onSort={toggleServicesSort} className="px-4 py-3 text-right">
                        Total
                      </SortableTableHeader>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Centro de Custo</th>
                      {(canDelete || canEdit) && <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ações</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {paginatedServices.map((item) => {
                      const header = contractMap.get(item.contract_id)
                      const contractLabel = header?.contract_number || header?.id || '—'
                      return (
                        <tr
                          key={item.id}
                          onClick={() => header && void openDetail(header)}
                          className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
                        >
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 max-w-[160px] truncate" title={header?.counterparty_name ?? ''}>
                            {header?.counterparty_name || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-[220px] truncate" title={item.description ?? ''}>
                            {item.description || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{item.quantity ?? '—'}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(item.unit_value)}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">{formatCurrency(item.total_value)}</td>
                          <td className="px-4 py-3 min-w-[160px]">
                            {item.category_id ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                                {categoryMap.get(item.category_id) ?? item.category_id}
                              </span>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 min-w-[160px]">
                            {item.cost_center_id ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                {costCenterMap.get(item.cost_center_id) ?? item.cost_center_id}
                              </span>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                            )}
                          </td>
                          {(canDelete || canEdit) && (
                            <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-center gap-2">
                                {canEdit && (
                                  <button
                                    type="button"
                                    title="Editar serviço"
                                    onClick={() => openServiceEdit(item, contractLabel)}
                                    className="text-primary-500 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                )}
                                {canDelete && (
                                  <button
                                    type="button"
                                    title="Excluir serviço"
                                    disabled={savingServiceId === item.id || !userId}
                                    onClick={() => void handleSoftDeleteService(item, contractLabel)}
                                    className="text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                {renderPagination(servicesPage, servicesPagesCount, sortedServices.length, setServicesPage)}
              </>
            )}
          </div>
        </>
      )}

      {/* Detalhe contrato */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-900 z-10">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                Contrato {detail.header.contract_number || detail.header.id.slice(0, 8)}
              </Heading>
              <div className="flex items-center gap-2">
                {canEdit && (
                  <Button variant="outline" size="sm" onClick={() => openContractEdit(detail.header)}>
                    Editar cabeçalho
                  </Button>
                )}
                <Button variant="ghost" onClick={() => setDetail(null)}>
                  Fechar
                </Button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {loadingDetail && (
                <div className="flex justify-center py-6">
                  <LoadingSpinner />
                </div>
              )}
              <div>
                <Text size="sm" className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  Dados do contrato
                </Text>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Número</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detail.header.contract_number || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Contraparte</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detail.header.counterparty_name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Documento</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDocument(detail.header.counterparty_document)}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Objetivo</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5 whitespace-pre-wrap">{detail.header.objective || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Data de assinatura</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDate(detail.header.signature_date)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Método de importação</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detail.header.import_method || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Contratante</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detail.header.contractor_name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Documento contratante</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDocument(detail.header.contractor_document)}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Obs. pagamento</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5 whitespace-pre-wrap">{detail.header.payment_notes || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Data pagamento</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDate(detail.header.payment_date)}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Observações manuais</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5 whitespace-pre-wrap break-words">{detail.header.manual_notes || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Início / Fim</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">
                      {formatDate(detail.header.start_date)} — {formatDate(detail.header.end_date)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Valor total</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5 font-semibold">{formatCurrency(detail.header.total_value)}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Observações</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5 whitespace-pre-wrap">{detail.header.notes || '—'}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Arquivo</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">
                      {(detail.header.file_name ||
                        hasAdmNotaDownloadKey(detail.header.file_name_storage, detail.header.file_name)) ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="flex items-center gap-1 text-sm">
                            <DocumentIcon className="h-4 w-4 text-gray-400 shrink-0" />
                            {detail.header.file_name ?? '—'}
                          </span>
                          {hasAdmNotaDownloadKey(detail.header.file_name_storage, detail.header.file_name) ? (
                            <button
                              type="button"
                              onClick={() =>
                                void openContractSignedDownload(
                                  detail.header.file_name_storage,
                                  detail.header.file_name
                                )
                              }
                              disabled={contractDocDownloadBusy}
                              className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                              Baixar documento
                            </button>
                          ) : null}
                        </div>
                      ) : (
                        '—'
                      )}
                    </dd>
                  </div>
                </dl>
              </div>
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Text size="sm" className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Serviços ({detail.services.length})
                  </Text>
                  {canEdit && (
                    <Button variant="outline" size="sm" onClick={() => void handleAddService()}>
                      <PlusIcon className="h-4 w-4 mr-1 inline" />
                      Adicionar serviço
                    </Button>
                  )}
                </div>
                {detail.services.length === 0 ? (
                  <Text size="sm" color="muted">
                    Nenhum serviço cadastrado.
                  </Text>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Descrição</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Código</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Qtd</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Vl. unit.</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Total</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Categoria</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Centro de Custo</th>
                          {(canDelete || canEdit) && (
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Ações</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                        {detail.services.map((item) => {
                          const contractLabel = detail.header.contract_number || detail.header.id
                          return (
                            <tr key={item.id}>
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{item.description || '—'}</td>
                              <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{item.service_code || '—'}</td>
                              <td className="px-3 py-2 text-right">{item.quantity ?? '—'}</td>
                              <td className="px-3 py-2 text-right">{formatCurrency(item.unit_value)}</td>
                              <td className="px-3 py-2 text-right font-medium">{formatCurrency(item.total_value)}</td>
                              <td className="px-3 py-2 min-w-[160px]">
                                {item.category_id ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                                    {categoryMap.get(item.category_id) ?? item.category_id}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                                )}
                              </td>
                              <td className="px-3 py-2 min-w-[160px]">
                                {item.cost_center_id ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                    {costCenterMap.get(item.cost_center_id) ?? item.cost_center_id}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                                )}
                              </td>
                              {(canDelete || canEdit) && (
                                <td className="px-3 py-2 text-center">
                                  <div className="flex items-center justify-center gap-2">
                                    {canEdit && (
                                      <button
                                        type="button"
                                        title="Editar"
                                        onClick={() => openServiceEdit(item, contractLabel)}
                                        className="text-primary-500 hover:text-primary-700"
                                      >
                                        <PencilIcon className="h-4 w-4" />
                                      </button>
                                    )}
                                    {canDelete && (
                                      <button
                                        type="button"
                                        title="Excluir"
                                        disabled={savingServiceId === item.id}
                                        onClick={() => void handleSoftDeleteService(item, contractLabel)}
                                        className="text-red-400 hover:text-red-600 disabled:opacity-50"
                                      >
                                        <TrashIcon className="h-4 w-4" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Editar cabeçalho contrato */}
      {contractEditModal && (
        <AdmDespesasContractEditModal
          modal={contractEditModal}
          submitting={submittingContractEdit}
          onChange={(updater) => setContractEditModal((prev) => (prev ? updater(prev) : prev))}
          onClose={() => !submittingContractEdit && setContractEditModal(null)}
          onSave={() => void handleSaveContractEdit()}
        />
      )}

      {/* Editar serviço */}
      {serviceEditModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
              Editar serviço contratado
            </Heading>
            <Text size="sm" color="muted" className="mb-4">
              Contrato: {serviceEditModal.contractLabel}
            </Text>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Descrição</label>
                <Input
                  value={serviceEditModal.description}
                  onChange={(e) => setServiceEditModal((p) => (p ? { ...p, description: e.target.value } : p))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Código do serviço</label>
                <Input
                  value={serviceEditModal.serviceCode}
                  onChange={(e) => setServiceEditModal((p) => (p ? { ...p, serviceCode: e.target.value } : p))}
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Qtd</label>
                  <Input
                    type="number"
                    step="any"
                    value={serviceEditModal.quantity}
                    onChange={(e) => setServiceEditModal((p) => (p ? { ...p, quantity: e.target.value } : p))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Vl. unit.</label>
                  <Input
                    type="number"
                    step="any"
                    value={serviceEditModal.unitValue}
                    onChange={(e) => setServiceEditModal((p) => (p ? { ...p, unitValue: e.target.value } : p))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Total</label>
                  <Input
                    type="number"
                    step="any"
                    value={serviceEditModal.totalValue}
                    onChange={(e) => setServiceEditModal((p) => (p ? { ...p, totalValue: e.target.value } : p))}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Categoria</label>
                <select
                  value={serviceEditModal.categoryId}
                  onChange={(e) => setServiceEditModal((p) => (p ? { ...p, categoryId: e.target.value } : p))}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm"
                >
                  <option value="">— Nenhuma —</option>
                  {categorias.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Centro de custo</label>
                <select
                  value={serviceEditModal.costCenterId}
                  onChange={(e) => setServiceEditModal((p) => (p ? { ...p, costCenterId: e.target.value } : p))}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm"
                >
                  <option value="">— Nenhum —</option>
                  {centrosCusto.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => !submittingServiceEdit && setServiceEditModal(null)} disabled={submittingServiceEdit}>
                Cancelar
              </Button>
              <Button variant="solid" onClick={() => void handleSaveServiceEdit()} disabled={submittingServiceEdit}>
                {submittingServiceEdit ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {printDialogOpen && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
              Imprimir registros
            </Heading>
            <Text size="sm" color="muted" className="mb-4">
              Selecione o escopo da impressão para a subaba atual ({subTab === 'contracts' ? 'Contratos' : 'Serviços'}).
            </Text>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => handleContractPrint('page')}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-left"
              >
                Página atual
              </button>
              <button
                type="button"
                onClick={() => handleContractPrint('filtered')}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-left"
              >
                Todos os filtrados
              </button>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                onClick={() => setPrintDialogOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exclusão contrato */}
      {deleteContractModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
              Confirmar exclusão
            </Heading>
            <Text size="sm" color="muted" className="mb-4">
              Excluindo: <strong className="text-gray-900 dark:text-white">{deleteContractModal.label}</strong>
              <br />
              Exclusão lógica do cabeçalho; o registro permanece no banco com marcação de exclusão.
            </Text>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Motivo <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              value={deleteContractReason}
              onChange={(e) => setDeleteContractReason(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm resize-none"
            />
            <div className="flex justify-end gap-3 mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteContractModal(null)
                  setDeleteContractReason('')
                }}
                disabled={submittingContractDelete}
              >
                Cancelar
              </Button>
              <Button
                variant="solid"
                className="!bg-red-600 hover:!bg-red-700"
                onClick={() => void confirmDeleteContract()}
                disabled={submittingContractDelete || !deleteContractReason.trim()}
              >
                {submittingContractDelete ? 'Excluindo...' : 'Confirmar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default AdmDespesasContractsTab
